import React, { useState } from 'react';
import { Modal, Button, Icon, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { ListCapitalizationInterface } from '../ListCapitalizationInitial.interface';
import { updateCapitalizationLenderInsurance } from '../../../services/capitalization';
import { successNotification, dangerNotification } from '../../../services/notification';
import { calculateAge } from '../../../utils/calculateAge';
import { extractApiErrorMessages } from '../../../utils/apiErrorHandler';

const InfoSection = styled.div`
  background: linear-gradient(135deg, #f8f5fc 0%, #fff 100%);
  border-radius: 12px;
  padding: 24px;
  margin: 16px 0;
  border: 1px solid #e0e0e0;
`;

const ProductBadge = styled.span`
  background: #59249b;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 12px;
`;

const ValueDisplay = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const ValueLabel = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
`;

const ValueAmount = styled.p`
  color: #59249b;
  font-size: 32px;
  font-weight: 700;
  margin: 0;
`;

const ValuePeriod = styled.span`
  color: #666;
  font-size: 16px;
  font-weight: 400;
`;

const ConditionsLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #59249b;
  font-size: 14px;
  text-decoration: none;
  padding: 12px;
  background: #f8f5fc;
  border-radius: 8px;
  margin-top: 16px;

  &:hover {
    background: #ede5f5;
    text-decoration: underline;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 14px;
  margin-bottom: 8px;

  .icon {
    color: #59249b;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #666;
  font-size: 13px;
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 13px;
  font-weight: 500;
`;

interface CapitalizationLenderInsuranceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  capitalization: ListCapitalizationInterface;
  rate?: number;
  isTenantPF?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const CapitalizationLenderInsuranceModal: React.FC<CapitalizationLenderInsuranceModalProps> = ({
  open,
  onClose,
  onSuccess,
  capitalization,
  rate,
  isTenantPF = true
}) => {
  const [loading, setLoading] = useState(false);
  const existingId = capitalization?.lenderInsuranceId;

  const tenantName = capitalization?.capitalizationTenant?.name || 'N/A';
  const propertyAddress = capitalization?.capitalizationProperty
    ? `${capitalization.capitalizationProperty.street}, ${capitalization.capitalizationProperty.number}`
    : 'N/A';

  const handleAction = async (adding: boolean) => {
    if (adding) {
      const tenantBirthDate = capitalization?.capitalizationTenant?.birthDate || '';
      if (tenantBirthDate) {
        const age = calculateAge(tenantBirthDate);
        if (age >= 65) {
          dangerNotification(
            'Erro',
            'Idade limite atingida: Produto válido apenas para clientes com menos de 65 anos.'
          );
          return;
        }
      }
    }

    setLoading(true);
    try {
      await updateCapitalizationLenderInsurance(capitalization.id, adding);
      successNotification(
        'Sucesso',
        `Seguro Prestamista ${adding ? 'adicionado' : 'removido'} com sucesso.`
      );
      onSuccess();
      onClose();
    } catch (error) {
      dangerNotification(
        'Erro',
        extractApiErrorMessages(error, 'Não foi possível atualizar o seguro prestamista.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Icon name="shield" color="purple" /> Seguro Prestamista
        <ProductBadge style={{ marginLeft: 12 }}>HDI Seguros</ProductBadge>
      </Modal.Header>

      <Modal.Content>
        {existingId ? (
          <>
            <Message warning icon>
              <Icon name="exclamation triangle" />
              <Message.Content>
                <Message.Header>Seguro Prestamista Ativo</Message.Header>
                Esta capitalização já possui o Seguro Prestamista incluído. Deseja removê-lo?
              </Message.Content>
            </Message>

            <InfoSection>
              <InfoRow>
                <InfoLabel>Locatário:</InfoLabel>
                <InfoValue>{tenantName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{propertyAddress}</InfoValue>
              </InfoRow>
              <ValueDisplay>
                <ValueLabel>Valor que será removido da cobrança:</ValueLabel>
                <ValueAmount>
                  {rate != null && rate !== undefined ? formatCurrency(rate) : 'N/A'}
                  <ValuePeriod>/mês</ValuePeriod>
                </ValueAmount>
              </ValueDisplay>
            </InfoSection>
          </>
        ) : (
          <>
            {!isTenantPF && (
              <Message warning icon>
                <Icon name="exclamation triangle" />
                <Message.Content>
                  Seguro Prestamista disponível apenas para locatários Pessoa Física (CPF).
                </Message.Content>
              </Message>
            )}

            <InfoSection>
              <ProductBadge>Proteção Completa</ProductBadge>

              <p style={{ color: '#333', marginBottom: 16 }}>
                O <strong>Seguro Prestamista HDI</strong> garante o pagamento do aluguel
                em caso de imprevistos, protegendo o inquilino e o proprietário.
              </p>

              <BenefitsList>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Cobertura em caso de morte
                </BenefitItem>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Cobertura em caso de invalidez permanente
                </BenefitItem>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Proteção contra desemprego involuntário
                </BenefitItem>
              </BenefitsList>

              <InfoRow>
                <InfoLabel>Locatário:</InfoLabel>
                <InfoValue>{tenantName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{propertyAddress}</InfoValue>
              </InfoRow>

              <ValueDisplay>
                <ValueLabel>Valor mensal:</ValueLabel>
                <ValueAmount>
                  {rate != null && rate !== undefined ? formatCurrency(rate) : 'N/A'}
                  <ValuePeriod>/mês</ValuePeriod>
                </ValueAmount>
              </ValueDisplay>
            </InfoSection>

            <ConditionsLink
              href="/documents/prestamista-hdi.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="file pdf outline" />
              Consultar Condições Gerais do Produto
            </ConditionsLink>
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        {!existingId && isTenantPF && (
          <Button
            primary
            color="purple"
            onClick={() => handleAction(true)}
            loading={loading}
          >
            <Icon name="check" /> Adicionar Seguro
          </Button>
        )}
        {existingId && (
          <Button
            color="red"
            onClick={() => handleAction(false)}
            loading={loading}
          >
            <Icon name="trash" /> Remover Seguro
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default CapitalizationLenderInsuranceModal;
